import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// TODO: derive these from the theme directly. Today they mirror tokens defined in:
//   • Tailwind v3 sources — styles/themes/atlas/variables/{zindex,spacing}.css and the
//     `borderRadius` / `spacing` blocks of tailwind.config.cjs.
//   • Tailwind v4 sources — `@theme inline { ... }` blocks in src/registry/themes/
//     {default,titan,throwback}/theme.css.
// The unit tests in cn.test.tsx enumerate every entry below — adding a token here
// without a matching test (or vice versa) is the brittle guard against drift.
const Z_TOKENS = ['below', 'base', 'popout', 'sticky', 'overlay', 'modal', 'toast'];

const SPACING_TOKENS = [
  'thin',
  'thin-default',
  'inline-xs',
  'inline-sm',
  'inline-md',
  'inline-default',
  'inline',
  'grid-xs',
  'grid-sm',
  'grid-default',
  'grid',
  'stack-xs',
  'stack-sm',
  'stack-md',
  'stack-default',
  'stack',
  'gutter-sm',
  'gutter-default',
  'gutter',
  'gutter-sm-bleed',
  'gutter-default-bleed',
  'gutter-bleed',
  'ckds-icon-size-default',
  'ckds-icon-size-sm',
  'ckds-icon-size-md',
  'ckds-icon-size-lg',
];

const RADIUS_TOKENS = [
  'ckds-radius-sm',
  'ckds-radius-default',
  'ckds-radius-lg',
  'ckds-radius-xl',
  'ckds-radius-round',
];

// Typography roles from `themes/*/components/typography.css`. These fail every stock
// font-size validator, so tailwind-merge classifies them as text COLOURS — which makes
// `cn('text-caption', 'text-ckds-text-fg-tier2')` look like two colours in conflict and
// silently drops the role. Registering them under `font-size` fixes both directions:
// a role and a colour token now coexist, while two roles still dedupe to the last.
// Deprecated aliases are included because they remain live call sites until UXD-148.
const TYPOGRAPHY_TOKENS = [
  // corrected names
  'display',
  'header-page',
  'header-section',
  'header-card',
  'header-count',
  'header-sub',
  'header-nav',
  'body-base',
  'body-sm',
  'caption',
  // deprecated aliases, still in use across consumers
  'body',
  'page-header',
  'category-header',
  'card-header',
  'count-header',
  'sub-header',
  'nav-header',
];

const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      // `theme.spacing` flows into padding, margin, gap, inset, space, translate, etc.
      // and is present in both tailwind-merge v2 and v3 — the cross-version safe entry.
      spacing: SPACING_TOKENS,
    },
    classGroups: {
      // z-index has no theme indirection, so register directly.
      z: [{ z: Z_TOKENS }],
      // Radius is registered at the class-group level (rather than `theme.borderRadius`)
      // because the theme key was renamed `borderRadius` → `radius` in tailwind-merge v3
      // (used by Tailwind v4 consumers of the registry). The class-group ID `rounded` is
      // stable across both versions.
      rounded: [{ rounded: RADIUS_TOKENS }],
      // Group ID `font-size` is stable across tailwind-merge v2 and v3, and is the group
      // that conflicts with `text-color` — registering here is what stops a role class
      // and a colour token cancelling each other out.
      'font-size': [{ text: TYPOGRAPHY_TOKENS }],
    },
  },
});

/**
 * Combines class names using clsx and tailwind-merge.
 *
 * This utility merges Tailwind CSS classes intelligently, handling conflicts
 * between utility classes (e.g., `p-2` and `p-4` will result in `p-4`).
 *
 * @param inputs - Class values to combine (strings, arrays, objects, etc.)
 * @returns A merged class string with Tailwind conflicts resolved
 *
 * @example
 * ```tsx
 * cn('p-2', 'p-4') // Returns 'p-4'
 * cn('text-red-500', isActive && 'text-blue-500') // Conditional classes
 * cn('base-class', { 'conditional-class': condition }) // Object syntax
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
